import {
  BaseRecord,
  HttpError,
  useInfiniteList,
  UseInfiniteListProps,
} from "@refinedev/core";
import { useEffect, useState, useMemo } from "react";

export const useAllList = <
  TQueryFnData extends BaseRecord = BaseRecord,
  TError extends HttpError = HttpError,
  TData extends BaseRecord = TQueryFnData,
>({
  resource,
  queryOptions = {},
  pagination = {
    pageSize: 20,
    mode: "server",
  },
}: UseInfiniteListProps<TQueryFnData, TError, TData>) => {
  const [shouldLoadMore, setShouldLoadMore] = useState(false);
  const pageSize = pagination?.pageSize || 20;
  const {
    data,
    isLoading,
    isError,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    refetch,
    isRefetching,
  } = useInfiniteList<TQueryFnData, TError, TData>({
    resource,
    pagination: {
      ...pagination,
      pageSize,
      mode: pagination?.mode || "server",
    },
    queryOptions: {
      ...queryOptions,
      getNextPageParam: (lastPage, allPages) => {
        // If we received fewer items than the page size, there are no more pages
        if (lastPage.data.length < pageSize) {
          return undefined;
        }
        return allPages.length + 1;
      },
    },
  });

  // Trigger loading more when initial load is complete
  useEffect(() => {
    if (!isLoading && hasNextPage && !shouldLoadMore) {
      setShouldLoadMore(true);
    }
  }, [isLoading, hasNextPage, shouldLoadMore]);

  // Load next page when shouldLoadMore is true
  useEffect(() => {
    if (shouldLoadMore && hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
        .then(() => {
          // Reset shouldLoadMore to false, the first effect will set it back to true if needed
          setShouldLoadMore(false);
        })
        .catch((error) => {
          console.error("Error fetching next page", error);
          setShouldLoadMore(false);
        });
    }
  }, [shouldLoadMore, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Flatten all pages into a single array (maintains backward compatibility)
  const allData = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page) => page.data);
  }, [data?.pages]);

  return {
    data: { data: allData, total: allData.length },
    isLoading: isLoading || isFetchingNextPage,
    refetch,
    isRefetching,
    isError,
  };
};
